import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Upload, Trash2, Download, FileText, User, Hash, MapPin, Building2, Clock, AlertTriangle, Archive, Sparkles, CheckCircle2, XCircle } from "lucide-react";
import AppHeader from "../components/AppHeader";
import { api, formatApiError } from "../lib/api";

const STATUS_COLORS = {
  borrador: "bg-stone-700 text-stone-300",
  extraccion_pendiente: "bg-amber-800 text-amber-200",
  revision_pendiente: "bg-orange-800 text-orange-100",
  comparables_pendientes: "bg-blue-800 text-blue-100",
  valorado: "bg-emerald-800 text-emerald-100",
  informe_generado: "bg-[#E0651F] text-white",
  archivado: "bg-stone-800 text-stone-500",
};

export default function ValoracionCaseDetail() {
  const { case_id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState("catastro");
  const [audit, setAudit] = useState([]);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, d, a] = await Promise.all([
        meta ? Promise.resolve({ data: meta }) : api.get("/admin/valoraciones/meta"),
        api.get(`/admin/valoraciones/cases/${case_id}`),
        api.get(`/admin/valoraciones/cases/${case_id}/audit?limit=30`),
      ]);
      setMeta(m.data);
      setDetail(d.data);
      setAudit(a.data.events || []);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error cargando expediente");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [case_id]);

  const patch = async (update) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/valoraciones/cases/${case_id}`, update);
      setDetail((d) => ({ ...d, case: data }));
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > (meta?.max_upload_mb || 25) * 1024 * 1024) {
      setErr(`Archivo supera ${meta?.max_upload_mb || 25} MB`);
      return;
    }
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/valoraciones/cases/${case_id}/documents?document_type=${uploadType}`,
        fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      await load();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error subiendo documento");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/admin/valoraciones/documents/${docId}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error eliminando");
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const resp = await api.get(`/admin/valoraciones/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: doc.mime_type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "documento";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error descargando");
    }
  };

  const archiveCase = async () => {
    if (!window.confirm("¿Archivar este expediente? Podrás verlo pero no editarlo activamente.")) return;
    try {
      await api.delete(`/admin/valoraciones/cases/${case_id}`);
      navigate("/admin/valoraciones");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error archivando");
    }
  };

  if (loading && !detail) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100">
        <AppHeader />
        <div className="flex items-center gap-2 text-stone-500 py-20 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Cargando…
        </div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="valoracion-detail">
        <AppHeader />
        <div className="max-w-2xl mx-auto py-20 px-6 text-center">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <div className="text-lg text-white mb-2">Expediente no encontrado</div>
          <div className="text-sm text-stone-500 mb-4">{err}</div>
          <Link to="/admin/valoraciones" className="text-[#E0651F] hover:underline">← Volver a Valoraciones</Link>
        </div>
      </div>
    );
  }

  const { case: c, documents } = detail;

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="valoracion-detail">
      <AppHeader />
      <header className="px-6 py-4 bg-stone-900 border-b border-stone-800">
        <div className="max-w-[1400px] mx-auto flex items-center gap-3 flex-wrap">
          <Link to="/admin/valoraciones" className="text-stone-500 hover:text-stone-300 flex items-center gap-1 text-xs uppercase tracking-[0.15em]">
            <ArrowLeft className="w-4 h-4" /> Valoraciones
          </Link>
          <span className="text-stone-600">·</span>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl font-black text-white truncate">
              {c.client_name_cache || "Sin cliente"} — {c.auction_id || "sin ID"}
            </div>
            <div className="text-xs text-stone-500 truncate">{c.address || c.cadastral_reference || "—"}</div>
          </div>
          <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-sm ${STATUS_COLORS[c.status] || "bg-stone-700"}`}>
            {meta?.status_labels?.[c.status] || c.status}
          </span>
          {saving && <Loader2 className="w-4 h-4 text-stone-500 animate-spin" />}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6 grid lg:grid-cols-3 gap-4">
        {/* LEFT: Metadatos del expediente */}
        <section className="lg:col-span-1 bg-stone-900 border border-stone-800 rounded-sm p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1">Datos del expediente</div>

          <PropRow icon={<User className="w-4 h-4" />} label="Cliente">
            <div className="text-sm text-stone-100">{c.client_name_cache || "—"}</div>
            <div className="text-[10px] text-stone-500 font-mono">{c.client_email_cache || ""}</div>
          </PropRow>

          <PropRow icon={<Hash className="w-4 h-4" />} label="ID subasta">
            <input
              defaultValue={c.auction_id || ""}
              onBlur={(e) => e.target.value !== c.auction_id && patch({ auction_id: e.target.value })}
              placeholder="SUB-…"
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-2 py-1 font-mono text-stone-100"
              data-testid="field-auction-id"
            />
          </PropRow>

          <PropRow icon={<Hash className="w-4 h-4" />} label="Ref. catastral">
            <input
              defaultValue={c.cadastral_reference || ""}
              onBlur={(e) => e.target.value !== c.cadastral_reference && patch({ cadastral_reference: e.target.value })}
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-2 py-1 font-mono uppercase text-stone-100"
              data-testid="field-cadastral"
            />
          </PropRow>

          <PropRow icon={<MapPin className="w-4 h-4" />} label="Dirección">
            <input
              defaultValue={c.address || ""}
              onBlur={(e) => e.target.value !== c.address && patch({ address: e.target.value })}
              placeholder="Se rellena automáticamente al extraer del catastro"
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-2 py-1 text-stone-100"
              data-testid="field-address"
            />
          </PropRow>

          <PropRow icon={<Building2 className="w-4 h-4" />} label="Tipo activo">
            <select
              value={c.asset_type}
              onChange={(e) => patch({ asset_type: e.target.value })}
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-2 py-1 text-stone-100 capitalize"
              data-testid="field-asset-type"
            >
              {meta?.asset_types?.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
            </select>
          </PropRow>

          <PropRow icon={<Clock className="w-4 h-4" />} label="Estado">
            <select
              value={c.status}
              onChange={(e) => patch({ status: e.target.value })}
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-2 py-1 text-stone-100"
              data-testid="field-status"
            >
              {meta?.statuses?.map((s) => <option key={s} value={s}>{meta.status_labels[s] || s}</option>)}
            </select>
          </PropRow>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1">Notas</div>
            <textarea
              defaultValue={c.notes || ""}
              rows={4}
              onBlur={(e) => e.target.value !== c.notes && patch({ notes: e.target.value })}
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-2 py-1 text-stone-100"
              data-testid="field-notes"
            />
          </div>

          <div className="text-[10px] text-stone-600 font-mono">
            Creado {c.created_at?.slice(0, 19).replace("T", " ")} · por {c.created_by || "?"}
          </div>

          {c.status !== "archivado" && (
            <button
              onClick={archiveCase}
              className="w-full flex items-center justify-center gap-1.5 border border-stone-700 hover:bg-red-950/50 hover:border-red-900 text-stone-400 hover:text-red-400 px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm transition-colors"
              data-testid="btn-archive"
            >
              <Archive className="w-3.5 h-3.5" /> Archivar expediente
            </button>
          )}
        </section>

        {/* CENTER: Documentos + Generar Informe */}
        <section className="lg:col-span-2 space-y-4">
          <div className="bg-stone-900 border border-stone-800 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
                Documentos del inmueble ({documents.length}/{meta?.max_uploads_per_case || 40})
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="bg-stone-800 border border-stone-700 rounded-sm text-xs px-2 py-1 text-stone-100"
                  data-testid="upload-type"
                >
                  {meta?.document_types?.map((t) => (
                    <option key={t} value={t}>{t.replace("_", " ")}</option>
                  ))}
                </select>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => uploadFile(e.target.files?.[0])}
                  className="hidden"
                  id="doc-upload"
                  data-testid="input-file-upload"
                />
                <label
                  htmlFor="doc-upload"
                  className={`inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold rounded-sm cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Subir documento
                </label>
              </div>
            </div>
            <div className="text-[11px] text-stone-500 mb-2">
              Adjunta el <b className="text-stone-300">Catastro</b>, <b className="text-stone-300">Valor de Referencia (VRC)</b>,
              <b className="text-stone-300"> nota simple</b>, <b className="text-stone-300">edicto</b> y el
              <b className="text-stone-300"> PDF del Portal del Notariado</b> (con el €/m² ponderado 12 meses).
            </div>
            {err && <div className="bg-red-950/40 border border-red-900 text-red-300 p-2 rounded-sm text-xs mb-2">{err}</div>}
            {documents.length === 0 ? (
              <div className="text-center py-10 text-stone-500 text-sm italic">
                Sin documentos aún. Sube los PDFs del inmueble para generar el informe.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-stone-500 text-[10px] uppercase tracking-[0.15em]">
                  <tr className="border-b border-stone-800">
                    <th className="text-left py-1.5 px-2">Tipo</th>
                    <th className="text-left py-1.5 px-2">Archivo</th>
                    <th className="text-right py-1.5 px-2">Tamaño</th>
                    <th className="text-left py-1.5 px-2">Subido</th>
                    <th className="px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d) => (
                    <tr key={d.id} className="border-b border-stone-800/70 hover:bg-stone-800/40" data-testid={`doc-row-${d.id}`}>
                      <td className="px-2 py-2">
                        <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 bg-stone-800 text-stone-300 rounded-sm">
                          {d.document_type?.replace("_", " ") || "otro"}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-stone-100 max-w-[250px] truncate">
                        <FileText className="w-3 h-3 inline text-stone-500 mr-1" />
                        {d.filename}
                      </td>
                      <td className="px-2 py-2 text-right text-stone-400 text-xs font-mono">
                        {(d.size / 1024).toFixed(0)} KB
                      </td>
                      <td className="px-2 py-2 text-stone-500 text-[10px] font-mono">
                        {d.created_at?.slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-2 py-2 flex items-center gap-1.5 justify-end">
                        <button onClick={() => downloadDoc(d)} className="text-stone-500 hover:text-stone-100" title="Descargar" data-testid={`btn-download-${d.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteDoc(d.id)} className="text-stone-500 hover:text-red-400" title="Eliminar" data-testid={`btn-delete-${d.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <AutoReportPanel caseId={case_id} documents={documents} onDone={load} caseData={c} />

          <ReportsPanel caseId={case_id} />

          {/* Auditoría */}
          <div className="bg-stone-900 border border-stone-800 rounded-sm p-4" data-testid="audit-log">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">
              Auditoría ({audit.length} eventos)
            </div>
            {audit.length === 0 ? (
              <div className="text-xs text-stone-600 italic">Sin eventos.</div>
            ) : (
              <ul className="space-y-1 text-xs max-h-64 overflow-y-auto">
                {audit.map((e) => (
                  <li key={e.id} className="flex items-center gap-2 text-stone-400">
                    <span className="text-[10px] font-mono text-stone-600 w-32 shrink-0">
                      {e.created_at?.slice(0, 19).replace("T", " ")}
                    </span>
                    <span className="text-stone-300 flex-1">{e.action}</span>
                    <span className="text-stone-500 text-[10px]">{e.user_email}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function PropRow({ icon, label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 flex items-center gap-1 mb-1">
        <span className="text-stone-600">{icon}</span> {label}
      </div>
      {children}
    </div>
  );
}


/* -----------------------------------------------------------------------
 * Botón único de generación automática con polling de progreso.
 * ----------------------------------------------------------------------- */
function AutoReportPanel({ caseId, documents, onDone, caseData }) {
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const [ccaa, setCcaa] = useState(caseData?.ccaa || "Madrid");
  const [reformZone, setReformZone] = useState(caseData?.reform_zone || "C");
  const [propertyState, setPropertyState] = useState(caseData?.property_state || "desconocido");
  const [showManual, setShowManual] = useState(false);

  const CCAA_LIST = [
    { code: "Madrid", label: "Madrid (ITP 6%)" },
    { code: "Andalucía", label: "Andalucía (ITP 7%)" },
    { code: "Cataluña", label: "Cataluña (ITP 10%)" },
    { code: "Valencia", label: "C. Valenciana (ITP 10%)" },
    { code: "Castilla y León", label: "Castilla y León (ITP 8%)" },
    { code: "Castilla-La Mancha", label: "Castilla-La Mancha (ITP 9%)" },
    { code: "Galicia", label: "Galicia (ITP 10%)" },
    { code: "País Vasco", label: "País Vasco (ITP 4-7%)" },
    { code: "Aragón", label: "Aragón (ITP 8%)" },
    { code: "Extremadura", label: "Extremadura (ITP 8%)" },
    { code: "Canarias", label: "Canarias (ITP 6,5%)" },
    { code: "Cantabria", label: "Cantabria (ITP 9%)" },
    { code: "Murcia", label: "Murcia (ITP 8%)" },
    { code: "Asturias", label: "Asturias (ITP 8%)" },
    { code: "Baleares", label: "Baleares (ITP 8%)" },
    { code: "La Rioja", label: "La Rioja (ITP 7%)" },
    { code: "Navarra", label: "Navarra (ITP 6%)" },
  ];
  const ZONES = [
    { code: "A", label: "A · Madrid, Barcelona, Baleares, Costa del Sol prime (400/1.000 €/m²)" },
    { code: "B", label: "B · Costa media, Málaga, Alicante, capitales medias (315/825 €/m²)" },
    { code: "C", label: "C · Capitales interiores, costa atlántica, residenciales (250/675 €/m²)" },
    { code: "D", label: "D · Interior rural, pueblos pequeños, baja liquidez (190/525 €/m²)" },
  ];
  const STATES = [
    { code: "buen_estado", label: "Buen estado — no requiere reforma relevante" },
    { code: "reforma_ligera", label: "Reforma ligera — actualización acabados/instalaciones" },
    { code: "reforma_integral", label: "Reforma integral — renovación completa" },
    { code: "ruina", label: "Ruina funcional — rehabilitación estructural" },
    { code: "desconocido", label: "Desconocido / sin visita interior" },
  ];

  const start = async () => {
    setRunning(true); setErr(""); setJob(null);
    try {
      const { data } = await api.post(
        `/admin/valoraciones/cases/${caseId}/auto-report`,
        null,
        { params: { ccaa, reform_zone: reformZone, property_state: propertyState } }
      );
      const jobId = data.job_id;
      setJob({ status: "queued", progress: 0, step: "Iniciando…" });
      // Poll cada 4s hasta 12 min (180 iteraciones). GPT-5.2 con web_search
      // y reasoning=medium tarda típicamente 3-6 min; margen amplio.
      for (let i = 0; i < 180; i++) {
        await new Promise(r => setTimeout(r, 4000));
        try {
          const { data: s } = await api.get(`/admin/valoraciones/cases/${caseId}/auto-report/${jobId}`);
          setJob(s);
          if (s.status === "done") { onDone(); return; }
          if (s.status === "error") { setErr(s.error || "Error desconocido"); return; }
        } catch { /* transient — reintenta */ }
      }
      setErr("Tiempo agotado (>12 min). Reintenta.");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error iniciando el informe automático");
    } finally {
      setRunning(false);
    }
  };

  const canStart = documents.length > 0 && !running;
  const progress = job?.progress || 0;
  const isDone = job?.status === "done";
  const isError = job?.status === "error" || !!err;

  return (
    <div className="bg-gradient-to-br from-[#E0651F]/10 to-stone-900 border border-[#E0651F]/40 rounded-sm p-5 space-y-4" data-testid="auto-report-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E0651F] flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" /> Valoración FM · GPT + Web Search
          </div>
          <div className="text-sm text-stone-200 mt-1 font-display font-bold">
            Sube los archivos, elige parámetros y pulsa un solo botón
          </div>
          <div className="text-xs text-stone-500 mt-0.5 max-w-2xl">
            OCR de los documentos → GPT-5.2 navega Idealista, Fotocasa, Pisos.com,
            Yaencontre, Habitaclia y RealAdvisor filtrando subastas/NPL/cesiones/nuda propiedad →
            aplica metodología FM (60/40, ±8%, 75% P50) → genera informe HTML corporativo con tu marca.
          </div>
        </div>
      </div>

      {/* Parámetros */}
      <div className="grid sm:grid-cols-3 gap-3 pt-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">Comunidad Autónoma</div>
          <select value={ccaa} onChange={(e) => setCcaa(e.target.value)}
            className="w-full bg-stone-900 border border-stone-700 rounded-sm text-xs px-2 py-2 text-stone-100"
            data-testid="select-ccaa">
            {CCAA_LIST.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">Zona reforma</div>
          <select value={reformZone} onChange={(e) => setReformZone(e.target.value)}
            className="w-full bg-stone-900 border border-stone-700 rounded-sm text-xs px-2 py-2 text-stone-100"
            data-testid="select-zone">
            {ZONES.map(z => <option key={z.code} value={z.code}>{z.label}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">Estado del inmueble</div>
          <select value={propertyState} onChange={(e) => setPropertyState(e.target.value)}
            className="w-full bg-stone-900 border border-stone-700 rounded-sm text-xs px-2 py-2 text-stone-100"
            data-testid="select-state">
            {STATES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Botones */}
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={start}
          disabled={!canStart}
          className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm shadow-lg shadow-[#E0651F]/20"
          data-testid="btn-auto-report"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {running ? "Generando…" : "Generar informe automático"}
        </button>
        <button
          onClick={() => setShowManual(true)}
          disabled={running}
          className="inline-flex items-center gap-1.5 bg-stone-800 hover:bg-stone-700 text-stone-100 px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm border border-stone-700"
          data-testid="btn-manual-report"
        >
          <FileText className="w-3.5 h-3.5" /> Usar mi Custom GPT
        </button>
      </div>

      {documents.length === 0 && (
        <div className="text-xs text-amber-300/80 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Sube documentos antes de generar el informe.
        </div>
      )}

      {job && (
        <div className="space-y-2" data-testid="auto-report-progress">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.15em] text-stone-400">
            <span>{job.step || job.status}</span>
            <span className="font-mono">{progress}%</span>
          </div>
          <div className="w-full h-2 bg-stone-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${isError ? "bg-red-500" : isDone ? "bg-emerald-500" : "bg-[#E0651F]"}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          {isDone && (
            <div className="bg-emerald-950/30 border border-emerald-900/60 rounded-sm p-3" data-testid="auto-report-result">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-white font-bold">Informe generado correctamente</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center text-xs">
                <div>
                  <div className="text-[9px] uppercase text-stone-500">Búsquedas web (GPT)</div>
                  <div className="text-[#E0651F] font-bold text-lg">{job.tool_calls || 0}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-stone-500">Tokens usados</div>
                  <div className="text-emerald-300 font-bold text-lg">{(job.total_tokens || 0).toLocaleString("es-ES")}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-stone-500">Coste estimado</div>
                  <div className="text-white font-bold text-lg">
                    ~{((job.total_tokens || 0) * 0.00002).toFixed(2)}€
                  </div>
                </div>
              </div>
              {(job.warnings || []).length > 0 && (
                <div className="text-[10px] text-amber-400 mt-2">
                  ⚠ {(job.warnings || []).join(" · ")}
                </div>
              )}
            </div>
          )}
          {isError && (
            <div className="bg-red-950/40 border border-red-900 rounded-sm p-3 flex items-start gap-2" data-testid="auto-report-error">
              <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <div className="text-xs text-red-200">{err || job.error}</div>
            </div>
          )}
        </div>
      )}

      {showManual && (
        <ManualReportModal caseId={caseId} onClose={() => setShowManual(false)} onDone={onDone} />
      )}
    </div>
  );
}


/* -----------------------------------------------------------------------
 * Modal "Usar mi Custom GPT": copia prompt + pega HTML devuelto
 * ----------------------------------------------------------------------- */
function ManualReportModal({ caseId, onClose, onDone }) {
  const [prompt, setPrompt] = useState("");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/valoraciones/cases/${caseId}/report-prompt`)
      .then(r => alive && setPrompt(r.data.prompt || ""))
      .catch(e => alive && setErr(formatApiError(e?.response?.data?.detail) || "Error cargando prompt"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [caseId]);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setErr("No se pudo copiar. Selecciona el texto manualmente.");
    }
  };

  const submit = async () => {
    if (!html.trim()) { setErr("Pega el HTML devuelto por tu Custom GPT"); return; }
    setSaving(true); setErr("");
    try {
      await api.post(`/admin/valoraciones/cases/${caseId}/manual-report`, { html });
      onDone();
      onClose();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error guardando el informe");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="manual-modal">
      <div className="bg-stone-950 border border-stone-700 rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-stone-950 border-b border-stone-800 px-5 py-3 flex items-center justify-between">
          <div className="font-display text-lg font-bold text-white">Usar mi Custom GPT</div>
          <button onClick={onClose} className="text-stone-500 hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-stone-400">
            <b className="text-white">1.</b> Copia este prompt (contiene los datos OCR del expediente) →{" "}
            <b className="text-white">2.</b> pégalo en tu Custom GPT{" "}
            <a href="https://chatgpt.com/g/g-68ca98b3e5948191a8aad056ed5680ac-valoracion-inmuebles-fm-subastas-investment"
               target="_blank" rel="noreferrer" className="text-[#E0651F] hover:underline">Valoración Inmuebles FM</a>{" "}
            → <b className="text-white">3.</b> copia el HTML que te devuelve y pégalo abajo.
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500">Prompt rellenado</div>
              <button onClick={copyPrompt}
                className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E0651F] hover:text-[#ff7a30] flex items-center gap-1"
                data-testid="btn-copy-prompt">
                {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                {copied ? "Copiado" : "Copiar prompt"}
              </button>
            </div>
            <textarea readOnly value={loading ? "Cargando…" : prompt} rows={8}
              className="w-full bg-stone-900 border border-stone-700 rounded-sm text-[11px] p-2 text-stone-300 font-mono"
              data-testid="prompt-textarea" />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 mb-1">HTML devuelto por tu GPT (pega aquí)</div>
            <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={10}
              placeholder="<!doctype html>..."
              className="w-full bg-stone-900 border border-stone-700 rounded-sm text-[11px] p-2 text-stone-100 font-mono"
              data-testid="html-textarea" />
          </div>

          {err && <div className="text-xs text-red-400">{err}</div>}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-400 hover:text-white px-3 py-2">
              Cancelar
            </button>
            <button onClick={submit} disabled={saving || !html.trim()}
              className="bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-40 text-white px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm"
              data-testid="btn-submit-manual">
              {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Guardar informe"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* -----------------------------------------------------------------------
 * Lista de informes previos + botón para abrirlos / imprimir a PDF
 * ----------------------------------------------------------------------- */
function ReportsPanel({ caseId }) {
  const [reports, setReports] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/valoraciones/cases/${caseId}/reports`);
      setReports(data.reports || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);
  useEffect(() => {
    // Refresca la lista periódicamente por si acaba de generarse
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, [caseId]);

  const download = async (r, fmt) => {
    try {
      const resp = await api.get(`/admin/valoraciones/reports/${r.id}/download`, {
        params: { format: fmt },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: fmt === "pdf" ? "application/pdf" : "text/html" });
      const url = window.URL.createObjectURL(blob);
      // Extraer filename del header Content-Disposition
      const cd = resp.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m?.[1] || `informe-fm.${fmt}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(`Error descargando ${fmt.toUpperCase()}: ${formatApiError(e?.response?.data?.detail) || e.message}`);
    }
  };

  const openInTab = async (r) => {
    try {
      const resp = await api.get(`/admin/valoraciones/reports/${r.id}`, { responseType: "text" });
      const blob = new Blob([resp.data], { type: "text/html;charset=utf-8" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      setErr("Error abriendo el informe");
    }
  };

  return (
    <div className="bg-stone-900 border border-stone-800 rounded-sm p-4" data-testid="reports-panel">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2 flex items-center gap-1.5">
        <FileText className="w-4 h-4" /> Informes generados ({reports.length})
      </div>
      {err && <div className="text-xs text-red-400 mb-2">{err}</div>}
      {reports.length === 0 ? (
        <div className="text-xs text-stone-500 italic text-center py-4">
          Sin informes todavía. Sube los documentos y pulsa &quot;Generar informe&quot;.
        </div>
      ) : (
        <ul className="space-y-1">
          {reports.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-xs text-stone-300 border-b border-stone-800 py-1.5" data-testid={`report-${r.id}`}>
              <FileText className="w-3.5 h-3.5 text-stone-500" />
              <span>Versión {r.version}</span>
              {r.auto && <span className="bg-[#E0651F] text-white text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-bold">Auto</span>}
              <span className="text-stone-600 font-mono text-[10px]">{r.created_at?.slice(0, 19).replace("T", " ")}</span>
              <span className="text-stone-600 text-[10px]">por {r.generated_by}</span>
              <button onClick={() => download(r, "pdf")}
                className="ml-auto inline-flex items-center gap-1 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-2 py-1 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm"
                data-testid={`btn-download-pdf-${r.id}`}>
                <Download className="w-3 h-3" /> PDF
              </button>
              <button onClick={() => download(r, "html")}
                className="inline-flex items-center gap-1 bg-stone-800 hover:bg-stone-700 text-stone-100 px-2 py-1 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm"
                data-testid={`btn-download-html-${r.id}`}>
                <Download className="w-3 h-3" /> HTML
              </button>
              <button onClick={() => openInTab(r)}
                className="inline-flex items-center gap-1 bg-stone-800 hover:bg-stone-700 text-stone-100 px-2 py-1 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm"
                data-testid={`btn-open-${r.id}`}>
                <FileText className="w-3 h-3" /> Ver
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
